import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/app/AppShell";
import AppEmptyState from "../../components/app/AppEmptyState";
import CallNumberCard from "../../components/CallNumberCard";
import { FullPageLoading } from "../../components/Loading";
import {
  listProfiles,
  listTranscripts,
  type ProfileSummary,
  type TranscriptItem,
} from "../../lib/api";
import { VITANARA_PHONE_TEL } from "../../lib/contact";
import { formatTimestamp } from "../../lib/format";

export default function AppCalls() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileSummary | null>(null);
  const [callNumber, setCallNumber] = useState<string>(VITANARA_PHONE_TEL);
  const [transcripts, setTranscripts] = useState<TranscriptItem[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await listProfiles();
        const p = (r.profiles || [])[0] || null;
        setProfile(p);
        if (r.callNumber) setCallNumber(r.callNumber);
        if (p) {
          const t = await listTranscripts(p.phone);
          setTranscripts(t.transcripts || []);
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Fehler beim Laden.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <FullPageLoading />;

  if (!profile) {
    return (
      <AppShell
        eyebrow="Mein Buch"
        title={
          <>
            Anrufe mit <em>Ihrem Erzähler</em>
          </>
        }
      >
        <AppEmptyState
          icon="calls"
          title="Noch kein Erzähler-Profil verknüpft."
          body="Anrufe gehören zu einem Erzähler-Profil, das wir per Absender-Rufnummer erkennen."
        />
      </AppShell>
    );
  }

  const firstName =
    profile?.interviewee_name?.split(" ")[0] || "Erzähler:in";

  return (
    <AppShell
      eyebrow="Mein Buch"
      title={
        <>
          Anrufe mit <em>{firstName}</em>
        </>
      }
    >
      <div style={{ padding: "0 22px 90px" }}>
        {error && <ErrorNote>{error}</ErrorNote>}

        {/* Call-in number card — Konrad & Ida only receive calls. */}
        <CallNumberCard
          callNumber={callNumber}
          intervieweeFirstName={
            profile?.interviewee_name?.split(" ")[0] || undefined
          }
        />

        <p
          style={{
            fontFamily: "var(--f-body)",
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--ink-faint)",
            margin: "12px 4px 22px",
            lineHeight: 1.5,
          }}
        >
          Konrad und Ida rufen niemanden an — sie nehmen Anrufe unter dieser
          Nummer entgegen. {firstName} entscheidet, wann es passt.
        </p>

        {/* Past calls — always shown, even when empty. */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 10,
          }}
        >
          <div className="t-caps">Vergangene Anrufe</div>
          <div
            style={{
              fontFamily: "var(--f-display)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
            }}
          >
            {transcripts.length === 0
              ? "noch keine"
              : `${transcripts.length} insgesamt`}
          </div>
        </div>

        {transcripts.length === 0 ? (
          <div
            style={{
              background: "var(--paper-50)",
              border: "1px solid rgba(43,26,15,0.10)",
              borderRadius: 12,
              padding: "20px 18px",
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 15,
              lineHeight: 1.5,
              color: "var(--ink-soft)",
            }}
          >
            Noch hat {firstName} nicht angerufen — die Leitung ist Tag und
            Nacht offen.
          </div>
        ) : (
          transcripts.map((t) => (
            <Link
              key={t.transcriptId}
              to={`/app/calls/${encodeURIComponent(t.transcriptId)}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "14px 16px",
                background: "var(--paper-50)",
                border: "1px solid rgba(43,26,15,0.10)",
                borderRadius: 12,
                marginBottom: 8,
                textDecoration: "none",
                color: "var(--ink)",
              }}
            >
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "var(--paper-200)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="var(--oxblood)"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontFamily: "var(--f-display)",
                    fontWeight: 500,
                    fontSize: 15,
                    color: "var(--ink)",
                  }}
                >
                  {formatTimestamp(t.timestamp) || "Anruf"}
                  {typeof t.duration_seconds === "number" && (
                    <span
                      style={{
                        fontFamily: "var(--f-ui)",
                        fontSize: 11,
                        color: "var(--ink-faint)",
                        marginLeft: 8,
                      }}
                    >
                      · {Math.round(t.duration_seconds / 60)} Min.
                    </span>
                  )}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-body)",
                    fontStyle: "italic",
                    fontSize: 13.5,
                    color: "var(--ink-soft)",
                    marginTop: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t.preview || "—"}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-ui)",
                    fontSize: 11,
                    color: "var(--oxblood)",
                    marginTop: 4,
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                  }}
                >
                  Anhören →
                </div>
              </div>
            </Link>
          ))
        )}
      </div>
    </AppShell>
  );
}

function ErrorNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="alert"
      style={{
        background: "rgba(107,32,24,0.08)",
        border: "1px solid rgba(107,32,24,0.25)",
        borderLeft: "3px solid var(--oxblood)",
        borderRadius: 4,
        padding: "10px 14px",
        color: "var(--oxblood-d)",
        fontFamily: "var(--f-body)",
        fontSize: 14,
        marginBottom: 18,
      }}
    >
      {children}
    </div>
  );
}
